import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { machineApi } from '../api/machines';
import { ticketApi } from '../api/tickets';
import { publicApi, PublicTicketResult } from '../api/public';
import { isValidEmail } from '../utils/validation';
import './ScanReport.css';

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** Znormalizowany widok maszyny — wspólny dla zalogowanego klienta i anonima. */
interface MachineView {
  id?: string; // tylko dla zalogowanego klienta (potrzebne do ticketApi.create)
  name: string;
  description: string | null;
  ticketSystemEnabled: boolean;
}

const ScanReport: React.FC = () => {
  const { qrSlug = '' } = useParams<{ qrSlug: string }>();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isCustomer = user?.roles?.includes('CUSTOMER');
  const isStaff = user?.roles?.includes('ADMIN') || user?.roles?.includes('EMPLOYEE_ADMIN') || user?.roles?.includes('EMPLOYEE');
  const isAnon = !user;

  const [machine, setMachine] = useState<MachineView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Ustawiane po udanym zgłoszeniu anonimowym — pokazujemy panel potwierdzenia (nie toast).
  const [submitted, setSubmitted] = useState<PublicTicketResult | null>(null);

  useEffect(() => {
    // Staff nie ma tu formularza — pokażemy dedykowany komunikat, nic nie ładujemy.
    if (user && isStaff) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = user
      // Zalogowany klient: endpoint z autoryzacją (pełna maszyna, w tym id).
      ? machineApi.getByQrSlug(qrSlug).then((res): MachineView => ({
          id: res.data.id,
          name: res.data.name,
          description: res.data.description ?? null,
          ticketSystemEnabled: res.data.ticketSystemEnabled,
        }))
      // Niezalogowany: publiczny endpoint z minimalnymi danymi.
      : publicApi.getMachineByQr(qrSlug).then((res): MachineView => ({
          name: res.data.name,
          description: res.data.description,
          ticketSystemEnabled: res.data.ticketSystemEnabled,
        }));

    load
      .then((m) => { if (!cancelled) setMachine(m); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) setLoadError('Nie znaleziono maszyny dla tego kodu QR.');
        else if (status === 403) setLoadError('Ta maszyna nie należy do Twojej firmy.');
        else setLoadError('Nie udało się załadować maszyny.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [qrSlug, user, isStaff]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files || []);
    const tooBig = list.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig) {
      setSubmitError(`Plik "${tooBig.name}" przekracza 10 MB`);
      e.target.value = '';
      return;
    }
    setSubmitError('');
    setFiles(list);
  };

  const submit = async () => {
    if (!machine) return;
    if (!title.trim() || !message.trim()) return;
    if (isAnon && !isValidEmail(email)) {
      setSubmitError('Podaj prawidłowy adres e-mail — to jedyny sposób powiązania zgłoszenia z Twoim kontem.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      if (isAnon) {
        const { data } = await publicApi.createTicket(qrSlug, {
          email: email.trim(),
          title: title.trim(),
          message: message.trim(),
          files,
        });
        setSubmitted(data);
      } else {
        // Zalogowany klient — dotychczasowy przepływ (z autoryzacją).
        const { data } = await ticketApi.create(machine.id!, {
          title: title.trim(),
          firstMessage: message.trim(),
          askAi: true,
        });
        for (const file of files) {
          try {
            await ticketApi.uploadAttachment(data.ticket.id, file);
          } catch (err) {
            console.error('Attachment upload failed', err);
          }
        }
        navigate(`/machines/${machine.id}?tab=tickets`, { replace: true });
      }
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || 'Nie udało się utworzyć zgłoszenia');
    } finally {
      setSubmitting(false);
    }
  };

  if (isStaff) {
    return (
      <div className="scan-page">
        <div className="scan-card">
          <h1 className="scan-title">Widok zgłoszenia</h1>
          <p>
            Ten widok jest dostępny tylko dla profilu typu Klient. Jako pracownik możesz przejść
            bezpośrednio do podglądu maszyny.
          </p>
          <div className="scan-actions">
            <Link className="btn btn-primary" to="/machines">Lista maszyn</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="scan-page">
        <div className="scan-card">Ładowanie...</div>
      </div>
    );
  }

  if (loadError || !machine) {
    return (
      <div className="scan-page">
        <div className="scan-card">
          <h1 className="scan-title">Nie można otworzyć zgłoszenia</h1>
          <p>{loadError || 'Brak danych maszyny.'}</p>
          <div className="scan-actions">
            {isAnon
              ? <Link className="btn btn-secondary" to="/login">Zaloguj się</Link>
              : <button className="btn btn-secondary" onClick={() => logout().then(() => navigate('/login'))}>Wyloguj</button>}
          </div>
        </div>
      </div>
    );
  }

  if (!machine.ticketSystemEnabled) {
    return (
      <div className="scan-page">
        <div className="scan-card">
          <h1 className="scan-title">Zgłoszenia niedostępne</h1>
          <p>System zgłoszeń jest wyłączony dla tej maszyny. Skontaktuj się z dostawcą, aby go włączyć.</p>
          <div className="scan-actions">
            {isAnon
              ? <Link className="btn btn-secondary" to="/login">Zaloguj się</Link>
              : <button className="btn btn-secondary" onClick={() => logout().then(() => navigate('/login'))}>Wyloguj</button>}
          </div>
        </div>
      </div>
    );
  }

  // Panel potwierdzenia po anonimowym zgłoszeniu (zamiast toasta).
  if (submitted) {
    return (
      <div className="scan-page">
        <div className="scan-card">
          <div className="scan-success-check" aria-hidden>✓</div>
          <h1 className="scan-title">Zgłoszenie wysłane</h1>
          <p className="scan-success-number">
            Nadaliśmy mu numer <strong>#{submitted.ticketNumber}</strong>
          </p>
          <p className="scan-meta" style={{ marginBottom: '0.5rem' }}>
            Dostawca maszyny zajmie się zgłoszeniem. Zanotuj jego numer.
          </p>
          <p className="scan-meta">
            Masz konto? <Link to="/login">Zaloguj się</Link>, aby śledzić zgłoszenia.
            Gdy założysz konto na adres <strong>{email.trim()}</strong>, to zgłoszenie
            pojawi się w Twojej historii.
          </p>
          <div className="scan-actions">
            <Link className="btn btn-primary" to="/login">Zaloguj się</Link>
            <button
              className="btn btn-secondary"
              onClick={() => {
                // Kolejne zgłoszenie tej samej maszyny — czyścimy formularz.
                setSubmitted(null);
                setTitle(''); setMessage(''); setFiles([]);
              }}
            >
              Nowe zgłoszenie
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canSubmit = !submitting
    && title.trim().length > 0
    && message.trim().length > 0
    && (!isAnon || isValidEmail(email));

  return (
    <div className="scan-page">
      <div className="scan-card">
        <div className="scan-eyebrow">Zgłoszenie usterki</div>
        <h1 className="scan-title">{machine.name}</h1>
        {machine.description && <p className="scan-meta">{machine.description}</p>}

        {submitError && <div className="auth-error">{submitError}</div>}

        {isAnon && (
          <div className="form-group">
            <label className="form-label">Twój e-mail</label>
            <input
              className="form-input"
              type="email"
              placeholder="np. jan.kowalski@firma.pl"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
            <div className="item-meta" style={{ marginTop: '0.375rem' }}>
              Na ten adres powiążemy zgłoszenie z Twoim kontem, gdy je założysz.
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Tytuł</label>
          <input
            className="form-input"
            placeholder="np. Maszyna nie startuje"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus={!isAnon}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Opis problemu</label>
          <textarea
            className="form-input"
            placeholder="Opisz, co się dzieje..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
          />
        </div>

        <div className="form-group">
          <label className="file-label">Załączniki (zdjęcia, max 10 MB każde)</label>
          <input type="file" multiple accept="image/*" className="form-input" onChange={onFileChange} />
          {files.length > 0 && (
            <div className="item-meta" style={{ marginTop: '0.375rem' }}>
              Wybrano: {files.map((f) => f.name).join(', ')}
            </div>
          )}
        </div>

        <button className="btn btn-primary scan-submit" onClick={submit} disabled={!canSubmit}>
          {submitting ? 'Wysyłanie...' : 'Wyślij zgłoszenie'}
        </button>

        <div className="scan-footer">
          {isAnon
            ? <Link className="link-button" to="/login">Masz konto? Zaloguj się</Link>
            : (
              <button className="link-button" onClick={() => logout().then(() => navigate('/login'))}>
                Wyloguj się
              </button>
            )}
        </div>
      </div>
    </div>
  );
};

export default ScanReport;
